import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import {
    LuArrowLeft, LuUserRound, LuBookOpen, LuMessageCircleQuestion, LuShieldAlert,
    LuArrowRightLeft, LuPlus, LuPencil, LuTrash2, LuGripVertical, LuStar,
    LuLightbulb, LuInfo, LuChevronUp, LuChevronDown, LuBot, LuX,
    LuRoute, LuUsers, LuArrowRight, LuTriangleAlert, LuPower, LuPowerOff, LuHistory,
    LuSmile, LuHand,
} from 'react-icons/lu';
import EmojiPicker, { Theme, type EmojiClickData } from 'emoji-picker-react';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/PageHeader';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { useCelular } from '../hooks/useCelular';
import Select, { iniciaisDe, type SelectOption } from '../components/Select';
import ConfirmationModal from '../components/ConfirmationModal';
import { TestAgentChat } from '../components/TestAgentChat';
import './AgentePage.css';
import {
    RegrasIAService, TOM_VOZ_OPTIONS, OBJETIVO_PRINCIPAL_OPTIONS,
    type IARule, type IAFaq, type ConfigAgente,
} from '../services/RegrasIAService';
import {
    RoteamentoIaService, TIPO_DESTINO, MAX_INTENCAO,
    type RegraRoteamento, type TipoDestino,
} from '../services/RoteamentoIaService';
import EquipeService from '../services/EquipeService';
import TipoUsuarioService from '../services/TipoUsuarioService';
import { apiFetch } from '../services/api';
import { acharModelo } from '../data/modelosDeAgente';

const API_URL = import.meta.env.VITE_API_URL || '';
const MAX_TEXTO = 2000;
/* A abertura é um balão de WhatsApp, não um texto de configuração: cabe menos. */
const MAX_ABERTURA = 1000;

const ABERTURA_EXEMPLO = [
    'Olá! Para te direcionar certinho, me diz o que você procura:',
    '1 - Saber sobre os produtos',
    '2 - Sou corretor(a)',
    '3 - Quero oferecer um produto ou serviço',
    '4 - Outro assunto',
].join('\n');

const SECOES = [
    { id: 'identidade', label: 'Identidade', icone: <LuUserRound size={14} /> },
    { id: 'abertura', label: 'Abertura', icone: <LuHand size={14} /> },
    { id: 'conhecimento', label: 'Conhecimento', icone: <LuBookOpen size={14} /> },
    { id: 'faq', label: 'Perguntas frequentes', icone: <LuMessageCircleQuestion size={14} /> },
    { id: 'regras', label: 'Regras', icone: <LuShieldAlert size={14} /> },
    { id: 'transbordo', label: 'Transbordo', icone: <LuArrowRightLeft size={14} /> },
    { id: 'roteamento', label: 'Roteamento', icone: <LuRoute size={14} /> },
    // Histórico (fase 5): dados do agente, regras, perguntas, roteamento, bot ligado/pausado.
    { id: 'historico', label: 'Histórico', icone: <LuHistory size={14} /> },
] as const;

type SecaoId = typeof SECOES[number]['id'];

const SOBRE_EMPRESA_EXEMPLO = 'Somos uma loja de roupas fundada em 2015, com foco em moda casual e preços acessíveis. Atendemos toda a região de Curitiba com entrega em até 3 dias úteis.';

const INSTRUCOES_SUGESTOES = [
    'Sempre perguntar o CEP',
    'Confirmar o tamanho/cor disponível antes de fechar o pedido',
    'Enviar link do produto para o cliente junto com as informações',
];

const TRANSBORDO_SUGESTOES = [
    'Cliente pediu explicitamente para falar com um atendente humano',
    'Cliente demonstrou interesse claro em fechar a compra',
    'Cliente fez uma pergunta fora da base de conhecimento cadastrada',
    'Cliente reclamou ou demonstrou insatisfação',
    'Após o cliente escolher um produto/serviço específico',
    'Cliente pediu desconto ou condição especial de pagamento',
];

const REGRA_SUGESTOES = [
    'Nunca ofereça descontos maiores que 10%',
    'Nunca prometa prazos de entrega',
    'Nunca invente informações sobre produtos que não existem',
];

const ROTEAMENTO_SUGESTOES = [
    'Vendas / dúvidas sobre produtos',
    'Troca, devolução ou produto com defeito',
    'Orçamento para empresas',
    'Financeiro: boleto, nota fiscal ou pagamento',
];

/* `destinoId` é string porque é o valor do `<Select>`; vira número só no envio.
   Vazio = nada escolhido ainda. */
interface RascunhoRoteamento { intencao: string; tipoDestino: TipoDestino; destinoId: string }
const ROTEAMENTO_VAZIO: RascunhoRoteamento = { intencao: '', tipoDestino: TIPO_DESTINO.Equipe, destinoId: '' };

/** O pedaço de `Usuario/BuscarTodos` que o seletor de pessoa usa. */
interface UsuarioApi {
    userId: number;
    userName: string;
    userEmail: string;
    userTypeuserid: number | null;
    userDeleted: boolean;
}

const FORM_VAZIO = {
    nomeAgente: '',
    tomVoz: TOM_VOZ_OPTIONS[0].value as number,
    objetivoPrincipal: OBJETIVO_PRINCIPAL_OPTIONS[0].value as number,
    sobreEmpresa: '',
    instrucoesAdicionais: '' as string | null,
    condicoesTransbordo: '',
    mensagemAbertura: '' as string | null,
    aberturaAtiva: false,
};

const rotuloDe = (opcoes: { value: number; label: string }[], valor: number) =>
    opcoes.find(o => o.value === valor)?.label ?? '';

const AgentePage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const celular = useCelular();
    const ehNovo = id === 'novo';
    const agentIdDaRota = ehNovo ? null : Number(id);

    /* Abaixo de 1180px o ensaio (`.ta`) sai da grade — sem largura ele vira uma
       conversa de 200px. No celular isso deixava a tela SEM nenhuma forma de
       testar o agente, então aqui ele volta como folha por cima: o botão só
       acende esta classe na raiz e o CSS faz o resto. */
    const [testando, setTestando] = useState(false);

    const [agentId, setAgentId] = useState<number | null>(agentIdDaRota);
    /* De qual modelo da biblioteca este agente nasceu. Vem da API, não da
       navegação: assim as perguntas sugeridas continuam disponíveis numa
       visita futura, e não só no minuto seguinte à criação. */
    const [origemModelo, setOrigemModelo] = useState<string | null>(null);
    const [ehPadrao, setEhPadrao] = useState(false);
    const [botAtivo, setBotAtivo] = useState(false);
    const [form, setForm] = useState(FORM_VAZIO);
    const [regras, setRegras] = useState<IARule[]>([]);
    const [faqs, setFaqs] = useState<IAFaq[]>([]);

    const [carregando, setCarregando] = useState(!ehNovo);
    const [salvando, setSalvando] = useState(false);
    const [alternandoBot, setAlternandoBot] = useState(false);
    const [definindoPadrao, setDefinindoPadrao] = useState(false);
    const [reordenando, setReordenando] = useState(false);

    const [secaoAtiva, setSecaoAtiva] = useState<SecaoId>('identidade');

    /* Edição na linha — no lugar das modais centralizadas de antes.
       `'nova'` é a linha em branco no fim da lista. */
    const [regraEmEdicao, setRegraEmEdicao] = useState<number | 'nova' | null>(null);
    const [textoRegra, setTextoRegra] = useState('');
    const [salvandoRegra, setSalvandoRegra] = useState(false);

    const [faqEmEdicao, setFaqEmEdicao] = useState<number | 'nova' | null>(null);
    const [rascunhoFaq, setRascunhoFaq] = useState({ pergunta: '', resposta: '' });
    const [salvandoFaq, setSalvandoFaq] = useState(false);

    /* Roteamento vem de outro endpoint (`RoteamentoIa`), não do agente: o
       `BuscarAgentePorId` não traz as regras de roteamento. */
    const [roteamentos, setRoteamentos] = useState<RegraRoteamento[]>([]);
    const [roteamentoEmEdicao, setRoteamentoEmEdicao] = useState<number | 'nova' | null>(null);
    const [rascunhoRoteamento, setRascunhoRoteamento] = useState<RascunhoRoteamento>(ROTEAMENTO_VAZIO);
    const [salvandoRoteamento, setSalvandoRoteamento] = useState(false);
    const [alternandoRoteamento, setAlternandoRoteamento] = useState<number | null>(null);

    /* Opções dos dois seletores de destino. Só carregam quando a seção de
       roteamento é aberta: são três chamadas que o resto da tela não usa. */
    const [destinos, setDestinos] = useState<{ pessoas: SelectOption[]; equipes: SelectOption[] } | null>(null);
    const [carregandoDestinos, setCarregandoDestinos] = useState(false);
    const pediuDestinos = useRef(false);

    const [confirmacao, setConfirmacao] = useState<{
        aberta: boolean; titulo: string; mensagem: string; aoConfirmar: () => void;
    }>({ aberta: false, titulo: '', mensagem: '', aoConfirmar: () => { } });

    const refInstrucoes = useRef<HTMLTextAreaElement | null>(null);
    const refAbertura = useRef<HTMLTextAreaElement | null>(null);
    const emojiRef = useRef<HTMLDivElement | null>(null);
    const [emojiAberto, setEmojiAberto] = useState(false);
    const { theme } = useTheme();
    const refColuna = useRef<HTMLDivElement | null>(null);
    const refRespostaFaq = useRef<HTMLTextAreaElement | null>(null);

    /* ─── Dados ──────────────────────────────────────────────── */

    const aplicar = (dados: ConfigAgente) => {
        setAgentId(dados.id);
        setOrigemModelo(dados.origemModelo ?? null);
        setEhPadrao(dados.isDefault ?? false);
        setBotAtivo(dados.botAtivo ?? false);
        setForm({
            nomeAgente: dados.nomeAgente || '',
            tomVoz: dados.tomVoz ?? (TOM_VOZ_OPTIONS[0].value as number),
            objetivoPrincipal: dados.objetivoPrincipal ?? (OBJETIVO_PRINCIPAL_OPTIONS[0].value as number),
            sobreEmpresa: dados.sobreEmpresa || '',
            instrucoesAdicionais: dados.instrucoesAdicionais || '',
            condicoesTransbordo: dados.condicoesTransbordo || '',
            mensagemAbertura: dados.mensagemAbertura || '',
            aberturaAtiva: dados.aberturaAtiva ?? false,
        });
        setRegras(dados.regras || []);
        setFaqs(dados.faqs || []);
    };

    const carregar = useCallback(async (idAlvo: number) => {
        setCarregando(true);
        try {
            const resposta = await RegrasIAService.getAgentePorId(idAlvo);
            if (resposta.sucesso && resposta.dados) {
                aplicar(resposta.dados);
            } else {
                aviso.erro(resposta.mensagem || 'Não foi possível carregar o agente.');
                navigate('/chat-rules');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
            navigate('/chat-rules');
        } finally {
            setCarregando(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (agentIdDaRota) carregar(agentIdDaRota);
    }, [agentIdDaRota, carregar]);

    const carregarRoteamento = useCallback(async (idAgente: number) => {
        try {
            const r = await RoteamentoIaService.porAgente(idAgente);
            if (r.sucesso) setRoteamentos(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar o roteamento.');
        } catch {
            aviso.erro('Não foi possível carregar o roteamento.');
        }
    }, []);

    useEffect(() => {
        if (agentId) carregarRoteamento(agentId);
    }, [agentId, carregarRoteamento]);

    const carregarDestinos = useCallback(async () => {
        pediuDestinos.current = true;
        setCarregandoDestinos(true);
        try {
            const [ru, tipos, equipes] = await Promise.all([
                apiFetch(`${API_URL}/api/Usuario/BuscarTodos`).then(r => r.json()),
                // Sem os tipos não dá para saber quem recebe lead; melhor oferecer
                // todos os ativos do que uma lista vazia. A API não exige isso.
                TipoUsuarioService.buscarTodos().catch(() => null),
                EquipeService.buscarTodas(),
            ]);

            const temUsuarios = !!ru?.sucesso && Array.isArray(ru.dados);
            const ativos = (temUsuarios ? ru.dados as UsuarioApi[] : []).filter(u => !u.userDeleted);
            const idsAtivos = new Set(ativos.map(u => u.userId));
            const recebemLead = tipos
                ? new Set(tipos.filter(t => t.canReceiveLead).map(t => t.id))
                : null;

            const pessoas: SelectOption[] = ativos
                .filter(u => !recebemLead || (u.userTypeuserid != null && recebemLead.has(u.userTypeuserid)))
                .sort((a, b) => a.userName.localeCompare(b.userName, 'pt-BR'))
                .map(u => ({
                    value: String(u.userId),
                    label: u.userName,
                    avatar: iniciaisDe(u.userName),
                    meta: u.userEmail,
                }));

            /* Equipe sem membro ativo a API recusa ("A equipe não tem membros
               ativos."): fica na lista, desabilitada, para a pessoa entender
               por que não aparece como escolha. Se os usuários não vieram, conta
               pelos ids da equipe — sem isso toda equipe pareceria vazia. */
            const opcoesEquipe: SelectOption[] = [...equipes]
                .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                .map(e => {
                    const membros = temUsuarios
                        ? e.membroIds.filter(id => idsAtivos.has(id)).length
                        : e.membroIds.length;
                    return {
                        value: String(e.id),
                        label: e.nome,
                        icon: <LuUsers size={13} />,
                        meta: membros === 0
                            ? 'Sem membros ativos'
                            : `${membros} ${membros === 1 ? 'membro' : 'membros'}`,
                        disabled: membros === 0,
                    };
                });

            setDestinos({ pessoas, equipes: opcoesEquipe });
        } catch {
            pediuDestinos.current = false;
            aviso.erro('Não foi possível carregar pessoas e equipes.');
        } finally {
            setCarregandoDestinos(false);
        }
    }, []);

    useEffect(() => {
        if (secaoAtiva === 'roteamento' && agentId && !pediuDestinos.current) carregarDestinos();
    }, [secaoAtiva, agentId, carregarDestinos]);

    /* ─── Cabeçalho ──────────────────────────────────────────── */

    const salvar = async () => {
        if (!form.nomeAgente.trim() || !form.sobreEmpresa.trim()) {
            aviso.atencao('Nome do agente e "sobre a empresa" são obrigatórios.');
            setSecaoAtiva(form.nomeAgente.trim() ? 'conhecimento' : 'identidade');
            return;
        }
        if (form.sobreEmpresa.length > MAX_TEXTO
            || (form.instrucoesAdicionais?.length || 0) > MAX_TEXTO
            || form.condicoesTransbordo.length > MAX_TEXTO) {
            aviso.atencao('Os textos longos têm limite de 2000 caracteres.');
            return;
        }
        if ((form.mensagemAbertura?.length || 0) > MAX_ABERTURA) {
            aviso.atencao(`A mensagem de abertura tem limite de ${MAX_ABERTURA} caracteres.`);
            setSecaoAtiva('abertura');
            return;
        }
        // Ligada e sem texto, a conversa nova ficaria sem primeira resposta.
        if (form.aberturaAtiva && !form.mensagemAbertura?.trim()) {
            aviso.atencao('Escreva a mensagem de abertura ou desligue a abertura fixa.');
            setSecaoAtiva('abertura');
            return;
        }

        setSalvando(true);
        try {
            if (agentId) {
                const r = await RegrasIAService.updateConfigAgente({ id: agentId, ...form });
                if (r.sucesso) { aviso.ok('Configuração salva.'); await carregar(agentId); }
                else aviso.erro(r.mensagem || 'Não foi possível salvar a configuração.');
            } else {
                const r = await RegrasIAService.createConfigAgente(form);
                if (r.sucesso && r.dados) {
                    aviso.ok('Agente criado.');
                    aplicar(r.dados);
                    // Sai de /novo para a rota do agente recém-criado, senão
                    // um F5 voltaria para o formulário em branco.
                    navigate(`/chat-rules/${r.dados.id}`, { replace: true });
                } else aviso.erro(r.mensagem || 'Não foi possível criar o agente.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    const descartar = () => {
        if (agentId) { carregar(agentId); aviso.info('Alterações descartadas.'); }
        else { setForm(FORM_VAZIO); }
    };

    const alternarBot = async () => {
        if (!agentId) { aviso.atencao('Salve o agente antes de ativá-lo.'); return; }
        const novo = !botAtivo;
        setAlternandoBot(true);
        try {
            const r = await RegrasIAService.toggleBot(agentId, novo);
            if (r.sucesso) { setBotAtivo(novo); aviso.ok(novo ? 'Agente respondendo.' : 'Agente pausado.'); }
            else aviso.erro(r.mensagem || 'Não foi possívelalterar o estado do bot.');
        } catch { aviso.erro(SEM_SERVIDOR); }
        finally { setAlternandoBot(false); }
    };

    const definirPadrao = async () => {
        if (!agentId || ehPadrao) return;
        setDefinindoPadrao(true);
        try {
            const r = await RegrasIAService.setAgentePadrao(agentId);
            if (r.sucesso) { setEhPadrao(true); aviso.ok('Agente definido como padrão da empresa.'); }
            else aviso.erro(r.mensagem || 'Não foi possível definir o agente padrão.');
        } catch { aviso.erro(SEM_SERVIDOR); }
        finally { setDefinindoPadrao(false); }
    };

    /* ─── Regras ─────────────────────────────────────────────── */

    const regrasOrdenadas = useMemo(
        () => [...regras].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)), [regras]);
    const faqsOrdenadas = useMemo(
        () => [...faqs].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)), [faqs]);

    const abrirRegra = (regra?: IARule) => {
        setRegraEmEdicao(regra ? regra.id : 'nova');
        setTextoRegra(regra?.descricaoRegra ?? '');
    };
    const fecharRegra = () => { setRegraEmEdicao(null); setTextoRegra(''); };

    const salvarRegra = async () => {
        if (!textoRegra.trim()) { aviso.atencao('O texto da regra não pode ficar vazio.'); return; }
        if (!agentId) { aviso.atencao('Salve o agente antes de adicionar regras.'); return; }
        setSalvandoRegra(true);
        try {
            const r = regraEmEdicao === 'nova'
                ? await RegrasIAService.create({
                    agentConfigId: agentId, descricaoRegra: textoRegra, ordem: regras.length + 1,
                })
                : await RegrasIAService.update({
                    id: regraEmEdicao as number,
                    descricaoRegra: textoRegra,
                    ordem: regrasOrdenadas.find(x => x.id === regraEmEdicao)?.ordem,
                });
            if (r.sucesso === false) aviso.erro(r.mensagem || 'Não foi possívelsalvar a regra.');
            else { aviso.ok('Regra salva.'); await carregar(agentId); fecharRegra(); }
        } catch { aviso.erro('Não foi possívelsalvar a regra.'); }
        finally { setSalvandoRegra(false); }
    };

    const excluirRegra = (idRegra: number) => {
        if (!agentId) return;
        setConfirmacao({
            aberta: true, titulo: 'Excluir regra',
            mensagem: 'Tem certeza que deseja excluir esta regra?',
            aoConfirmar: async () => {
                try {
                    const r = await RegrasIAService.delete(idRegra);
                    if (r.sucesso === false) aviso.erro(r.mensagem || 'Não foi possívelexcluir a regra.');
                    else { aviso.ok('Regra excluída.'); await carregar(agentId); }
                } catch { aviso.erro('Não foi possívelexcluir a regra.'); }
                setConfirmacao(p => ({ ...p, aberta: false }));
            },
        });
    };

    const moverRegra = async (indice: number, direcao: 'cima' | 'baixo') => {
        if (!agentId) return;
        const alvo = direcao === 'cima' ? indice - 1 : indice + 1;
        if (alvo < 0 || alvo >= regrasOrdenadas.length) return;
        const atual = regrasOrdenadas[indice];
        const outra = regrasOrdenadas[alvo];
        setReordenando(true);
        try {
            await Promise.all([
                RegrasIAService.update({ id: atual.id, descricaoRegra: atual.descricaoRegra, ordem: outra.ordem ?? alvo + 1 }),
                RegrasIAService.update({ id: outra.id, descricaoRegra: outra.descricaoRegra, ordem: atual.ordem ?? indice + 1 }),
            ]);
            await carregar(agentId);
        } catch { aviso.erro('Não foi possívelreordenar a regra.'); }
        finally { setReordenando(false); }
    };

    /* ─── FAQ ────────────────────────────────────────────────── */

    const abrirFaq = (faq?: IAFaq) => {
        setFaqEmEdicao(faq ? faq.id : 'nova');
        setRascunhoFaq({ pergunta: faq?.pergunta ?? '', resposta: faq?.resposta ?? '' });
    };
    const fecharFaq = () => { setFaqEmEdicao(null); setRascunhoFaq({ pergunta: '', resposta: '' }); };

    /* Pergunta sugerida pelo modelo: abre a linha nova já com a pergunta e leva
       o foco para a RESPOSTA, que é a parte que só a empresa sabe. O modelo
       nunca cria a FAQ sozinho — pergunta sem resposta viraria "R:" vazio no
       prompt (PromptBuilderService). */
    const sugerirFaq = (pergunta: string) => {
        setFaqEmEdicao('nova');
        setRascunhoFaq({ pergunta, resposta: '' });
        requestAnimationFrame(() => refRespostaFaq.current?.focus());
    };

    const salvarFaq = async () => {
        if (!rascunhoFaq.pergunta.trim() || !rascunhoFaq.resposta.trim()) {
            aviso.atencao('Pergunta e resposta são obrigatórias.'); return;
        }
        if (!agentId) { aviso.atencao('Salve o agente antes de adicionar perguntas.'); return; }
        setSalvandoFaq(true);
        try {
            const r = faqEmEdicao === 'nova'
                ? await RegrasIAService.createFaq({
                    agentConfigId: agentId, ...rascunhoFaq, ordem: faqs.length + 1,
                })
                : await RegrasIAService.updateFaq({
                    id: faqEmEdicao as number, ...rascunhoFaq,
                    ordem: faqsOrdenadas.find(x => x.id === faqEmEdicao)?.ordem,
                });
            if (r.sucesso === false) aviso.erro(r.mensagem || 'Não foi possívelsalvar a pergunta.');
            else { aviso.ok('Pergunta salva.'); await carregar(agentId); fecharFaq(); }
        } catch { aviso.erro('Não foi possívelsalvar a pergunta.'); }
        finally { setSalvandoFaq(false); }
    };

    const excluirFaq = (idFaq: number) => {
        if (!agentId) return;
        setConfirmacao({
            aberta: true, titulo: 'Excluir pergunta',
            mensagem: 'Tem certeza que deseja excluir esta pergunta?',
            aoConfirmar: async () => {
                try {
                    const r = await RegrasIAService.deleteFaq(idFaq);
                    if (r.sucesso === false) aviso.erro(r.mensagem || 'Não foi possívelexcluir a pergunta.');
                    else { aviso.ok('Pergunta excluída.'); await carregar(agentId); }
                } catch { aviso.erro('Não foi possívelexcluir a pergunta.'); }
                setConfirmacao(p => ({ ...p, aberta: false }));
            },
        });
    };

    const moverFaq = async (indice: number, direcao: 'cima' | 'baixo') => {
        if (!agentId) return;
        const alvo = direcao === 'cima' ? indice - 1 : indice + 1;
        if (alvo < 0 || alvo >= faqsOrdenadas.length) return;
        const atual = faqsOrdenadas[indice];
        const outra = faqsOrdenadas[alvo];
        setReordenando(true);
        try {
            await Promise.all([
                RegrasIAService.updateFaq({ id: atual.id, pergunta: atual.pergunta, resposta: atual.resposta, ordem: outra.ordem ?? alvo + 1 }),
                RegrasIAService.updateFaq({ id: outra.id, pergunta: outra.pergunta, resposta: outra.resposta, ordem: atual.ordem ?? indice + 1 }),
            ]);
            await carregar(agentId);
        } catch { aviso.erro('Não foi possívelreordenar a pergunta.'); }
        finally { setReordenando(false); }
    };

    /* ─── Roteamento ─────────────────────────────────────────── */

    const abrirRoteamento = (regra?: RegraRoteamento) => {
        setRoteamentoEmEdicao(regra ? regra.id : 'nova');
        setRascunhoRoteamento(regra
            ? {
                intencao: regra.intencao,
                tipoDestino: regra.tipoDestino,
                // Destino inválido não volta pré-escolhido: a pessoa abriu
                // justamente para trocá-lo.
                destinoId: regra.destinoValido ? String(regra.destinoId) : '',
            }
            : ROTEAMENTO_VAZIO);
        if (!destinos && !carregandoDestinos) carregarDestinos();
    };
    const fecharRoteamento = () => { setRoteamentoEmEdicao(null); setRascunhoRoteamento(ROTEAMENTO_VAZIO); };

    /* Trocar entre equipe e pessoa limpa a escolha: o id de uma equipe não
       significa nada na lista de pessoas. */
    const trocarTipoDestino = (tipo: TipoDestino) => setRascunhoRoteamento(p =>
        p.tipoDestino === tipo ? p : { ...p, tipoDestino: tipo, destinoId: '' });

    const roteamentoAberto = typeof roteamentoEmEdicao === 'number'
        ? roteamentos.find(r => r.id === roteamentoEmEdicao) ?? null
        : null;

    /* O destino atual pode não estar na lista: uma pessoa escolhida antes e
       que hoje não recebe lead continua válida para a API, mas sumiria do
       seletor e a edição abriria em branco. Ela volta como opção própria. */
    const opcoesDestino = useMemo<SelectOption[]>(() => {
        const ehEquipe = rascunhoRoteamento.tipoDestino === TIPO_DESTINO.Equipe;
        const base = destinos ? (ehEquipe ? destinos.equipes : destinos.pessoas) : [];
        const atual = roteamentoAberto;
        if (!atual || !atual.destinoValido || atual.tipoDestino !== rascunhoRoteamento.tipoDestino) return base;
        if (base.some(o => o.value === String(atual.destinoId))) return base;
        return [{
            value: String(atual.destinoId),
            label: atual.destinoNome,
            meta: 'Destino atual',
            ...(ehEquipe ? { icon: <LuUsers size={13} /> } : { avatar: iniciaisDe(atual.destinoNome) }),
        }, ...base];
    }, [destinos, rascunhoRoteamento.tipoDestino, roteamentoAberto]);

    const salvarRoteamento = async () => {
        const intencao = rascunhoRoteamento.intencao.trim();
        const ehEquipe = rascunhoRoteamento.tipoDestino === TIPO_DESTINO.Equipe;
        if (!intencao) { aviso.atencao('Descreva a intenção.'); return; }
        if (intencao.length > MAX_INTENCAO) {
            aviso.atencao(`A intenção deve ter no máximo ${MAX_INTENCAO} caracteres.`); return;
        }
        if (!rascunhoRoteamento.destinoId) { aviso.atencao(ehEquipe ? 'Escolha a equipe.' : 'Escolha a pessoa.'); return; }
        if (!agentId) { aviso.atencao('Salve o agente antes de configurar o roteamento.'); return; }

        const destino = {
            intencao,
            tipoDestino: rascunhoRoteamento.tipoDestino,
            destinoId: Number(rascunhoRoteamento.destinoId),
        };

        setSalvandoRoteamento(true);
        try {
            const r = roteamentoEmEdicao === 'nova'
                ? await RoteamentoIaService.criar({ agentConfigId: agentId, ...destino })
                : await RoteamentoIaService.atualizar({
                    id: roteamentoEmEdicao as number,
                    ...destino,
                    // Editar não liga uma regra desligada — isso é o botão da linha.
                    ativa: roteamentoAberto?.ativa ?? true,
                });
            if (r.sucesso === false) aviso.erro(r.mensagem || 'Não foi possível salvar o roteamento.');
            else { aviso.ok('Roteamento salvo.'); await carregarRoteamento(agentId); fecharRoteamento(); }
        } catch { aviso.erro('Não foi possível salvar o roteamento.'); }
        finally { setSalvandoRoteamento(false); }
    };

    /* A API revalida o destino em todo `Atualizar`, inclusive aqui. Por isso
       a linha com destino inválido não oferece este botão — a chamada seria
       recusada com "Pessoa/Equipe não encontrada". */
    const alternarRoteamento = async (regra: RegraRoteamento) => {
        if (!agentId) return;
        setAlternandoRoteamento(regra.id);
        try {
            const r = await RoteamentoIaService.atualizar({
                id: regra.id,
                intencao: regra.intencao,
                tipoDestino: regra.tipoDestino,
                destinoId: regra.destinoId,
                ativa: !regra.ativa,
            });
            if (r.sucesso === false) aviso.erro(r.mensagem || 'Não foi possível alterar a regra.');
            else {
                aviso.ok(regra.ativa ? 'Regra desativada.' : 'Regra ativada.');
                await carregarRoteamento(agentId);
            }
        } catch { aviso.erro('Não foi possível alterar a regra.'); }
        finally { setAlternandoRoteamento(null); }
    };

    const excluirRoteamento = (idRegra: number) => {
        if (!agentId) return;
        setConfirmacao({
            aberta: true, titulo: 'Excluir roteamento',
            mensagem: 'Tem certeza que deseja excluir esta regra de roteamento? As conversas desse assunto voltam para o rodízio normal.',
            aoConfirmar: async () => {
                try {
                    const r = await RoteamentoIaService.remover(idRegra);
                    if (r.sucesso === false) aviso.erro(r.mensagem || 'Não foi possível excluir a regra.');
                    else { aviso.ok('Regra excluída.'); await carregarRoteamento(agentId); }
                } catch { aviso.erro('Não foi possível excluir a regra.'); }
                setConfirmacao(p => ({ ...p, aberta: false }));
            },
        });
    };

    /* ─── Abertura ───────────────────────────────────────────── */

    // Mesmo gesto das sugestões: entra onde o cursor está, não no fim do texto.
    const inserirNaAbertura = (texto: string) => {
        const area = refAbertura.current;
        const atual = form.mensagemAbertura || '';
        if (atual.length + texto.length > MAX_ABERTURA) return;
        if (!area) {
            setForm(p => ({ ...p, mensagemAbertura: atual + texto }));
            return;
        }
        const ini = area.selectionStart ?? atual.length;
        const fim = area.selectionEnd ?? atual.length;
        setForm(p => ({ ...p, mensagemAbertura: `${atual.slice(0, ini)}${texto}${atual.slice(fim)}` }));
        requestAnimationFrame(() => {
            area.focus();
            const cursor = ini + texto.length;
            area.setSelectionRange(cursor, cursor);
        });
    };

    const inserirEmoji = (dado: EmojiClickData) => inserirNaAbertura(dado.emoji);

    useEffect(() => {
        if (!emojiAberto) return;
        const fora = (e: MouseEvent) => {
            if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setEmojiAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [emojiAberto]);

    /* ─── Sugestões ──────────────────────────────────────────── */

    const inserirInstrucao = (texto: string) => {
        const area = refInstrucoes.current;
        const atual = form.instrucoesAdicionais || '';
        if (!area) {
            setForm(p => ({ ...p, instrucoesAdicionais: atual ? `${atual} ${texto}` : texto }));
            return;
        }
        const ini = area.selectionStart ?? atual.length;
        const fim = area.selectionEnd ?? atual.length;
        setForm(p => ({ ...p, instrucoesAdicionais: `${atual.slice(0, ini)}${texto}${atual.slice(fim)}` }));
        requestAnimationFrame(() => {
            area.focus();
            const cursor = ini + texto.length;
            area.setSelectionRange(cursor, cursor);
        });
    };

    const somarTransbordo = (texto: string) => setForm(p => ({
        ...p,
        condicoesTransbordo: p.condicoesTransbordo ? `${p.condicoesTransbordo}; ${texto}` : texto,
    }));

    /* ─── Navegação entre seções ─────────────────────────────── */

    /* Cada seção é uma tela própria: clicar no trilho TROCA a seção, em vez
       de rolar até ela. Foi pedido assim depois de ver a versão rolável —
       "eu prefiro clicar em cada uma e ai entrar nas configurações da seção".
       Some junto o observador de rolagem que mantinha o trilho em dia. */
    const irPara = (secao: SecaoId) => {
        setSecaoAtiva(secao);
        refColuna.current?.scrollTo({ top: 0 });
    };

    const listasBloqueadas = !agentId;

    const resumo = [
        rotuloDe(TOM_VOZ_OPTIONS as any, form.tomVoz),
        rotuloDe(OBJETIVO_PRINCIPAL_OPTIONS as any, form.objetivoPrincipal),
        `${regras.length} ${regras.length === 1 ? 'regra' : 'regras'}`,
        `${faqs.length} ${faqs.length === 1 ? 'pergunta' : 'perguntas'}`,
    ].filter(Boolean).join(' · ');

    /* Comparação por texto normalizado: a pessoa pode ter escrito a mesma
       pergunta à mão, com outra caixa ou espaço sobrando, e a sugestão
       repetida ao lado da FAQ pronta pareceria bug. */
    const modeloDeOrigem = acharModelo(origemModelo);
    const perguntasSugeridas = useMemo(() => {
        if (!modeloDeOrigem) return [];
        const normalizar = (t: string) => t.trim().toLowerCase().replace(/\s+/g, ' ');
        const jaExistem = new Set(faqs.map(f => normalizar(f.pergunta)));
        return modeloDeOrigem.perguntasSugeridas.filter(p => !jaExistem.has(normalizar(p)));
    }, [modeloDeOrigem, faqs]);

    const contagemDaSecao = (secao: SecaoId) =>
        secao === 'regras' ? regras.length
            : secao === 'faq' ? faqs.length
                : secao === 'roteamento' ? roteamentos.length
                    : null;

    /* O mesmo formulário serve a linha nova e a linha em edição — só a nova
       oferece as sugestões, como nas Regras. */
    const formularioRoteamento = (nova: boolean) => {
        const ehEquipe = rascunhoRoteamento.tipoDestino === TIPO_DESTINO.Equipe;
        const semEquipes = !!destinos && destinos.equipes.length === 0;
        return (
            <div className="ax-li-form">
                <label className="ax-lab" htmlFor="rt-intencao">Quando o motivo do contato for</label>
                {nova && (
                    <div className="ax-chips">
                        {ROTEAMENTO_SUGESTOES.map(t => (
                            <button key={t} type="button" className="ax-chip"
                                onClick={() => setRascunhoRoteamento(p => ({ ...p, intencao: t }))}>
                                <LuLightbulb size={12} /> {t}
                            </button>
                        ))}
                    </div>
                )}
                <textarea
                    id="rt-intencao" className="ax-ta" rows={2} autoFocus
                    maxLength={MAX_INTENCAO}
                    value={rascunhoRoteamento.intencao}
                    placeholder="Ex.: Troca, devolução ou produto com defeito"
                    onChange={e => setRascunhoRoteamento(p => ({ ...p, intencao: e.target.value }))}
                />
                <span className="ax-conta">{rascunhoRoteamento.intencao.length} / {MAX_INTENCAO}</span>

                <span className="ax-lab" id="rt-destino-lab">Encaminhar para</span>
                <div className="ax-rt-destino">
                    <div className="ax-seg" role="radiogroup" aria-labelledby="rt-destino-lab">
                        <button type="button" role="radio" aria-checked={ehEquipe}
                            className={ehEquipe ? 'is-active' : ''}
                            onClick={() => trocarTipoDestino(TIPO_DESTINO.Equipe)}>
                            <LuUsers size={12} /> Equipe
                        </button>
                        <button type="button" role="radio" aria-checked={!ehEquipe}
                            className={!ehEquipe ? 'is-active' : ''}
                            onClick={() => trocarTipoDestino(TIPO_DESTINO.Usuario)}>
                            <LuUserRound size={12} /> Pessoa
                        </button>
                    </div>
                    <div className="ax-rt-sel">
                        <Select
                            value={rascunhoRoteamento.destinoId}
                            options={opcoesDestino}
                            onChange={v => setRascunhoRoteamento(p => ({ ...p, destinoId: v }))}
                            placeholder={carregandoDestinos ? 'Carregando…' : ehEquipe ? 'Escolha a equipe' : 'Escolha a pessoa'}
                            disabled={carregandoDestinos}
                            searchFrom={6}
                            showOptionAccessory
                            portal
                            ariaLabel={ehEquipe ? 'Equipe de destino' : 'Pessoa de destino'}
                            emptyText={!destinos
                                ? 'Não foi possível carregar. Feche e abra de novo.'
                                : ehEquipe ? 'Nenhuma equipe cadastrada.' : 'Nenhuma pessoa que recebe lead.'}
                        />
                    </div>
                </div>
                {ehEquipe && semEquipes ? (
                    <span className="ax-hint">
                        A empresa ainda não tem equipes.{' '}
                        <button type="button" className="ax-link" onClick={() => navigate('/equipes')}>Cadastrar equipes</button>
                        {' '}ou escolha uma pessoa.
                    </span>
                ) : (
                    <span className="ax-hint">
                        {ehEquipe
                            ? 'A conversa vai para o membro com menos atendimentos em aberto. O líder só recebe se também for membro.'
                            : 'Só aparecem pessoas que podem receber lead.'}
                    </span>
                )}

                <div className="ax-li-acts">
                    <button type="button" className="ax-btn" onClick={fecharRoteamento}>Cancelar</button>
                    <button type="button" className="ax-btn is-primary" onClick={salvarRoteamento} disabled={salvandoRoteamento}>
                        {salvandoRoteamento ? 'Salvando…' : 'Salvar roteamento'}
                    </button>
                </div>
            </div>
        );
    };

    /* As seções que gravam por linha precisam do id do agente. */
    const precisaDoAgente = (secao: SecaoId) => secao === 'faq' || secao === 'regras' || secao === 'roteamento' || secao === 'historico';

    return (
        <div className="page-shell ax-screen">
            <PageHeader
                leading={
                    <button type="button" className="ax-voltar" onClick={() => navigate('/chat-rules')}>
                        <LuArrowLeft size={14} /> Agentes
                    </button>
                }
                title={ehNovo && !agentId ? 'Novo agente' : (form.nomeAgente || 'Agente')}
                subtitle={ehNovo && !agentId ? 'Preencha a identidade e salve para liberar FAQ, regras e roteamento.' : resumo}
                actions={
                    <div className="ax-acts">
                        {agentId && (ehPadrao
                            ? <span className="ax-badge is-star"><LuStar size={11} /> Padrão da empresa</span>
                            : <button type="button" className="ax-btn" onClick={definirPadrao} disabled={definindoPadrao}>
                                <LuStar size={13} /> {definindoPadrao ? 'Definindo…' : 'Definir como padrão'}
                            </button>)}

                        <button
                            type="button"
                            className={`ax-switch ${botAtivo ? 'is-on' : ''}`}
                            onClick={alternarBot}
                            disabled={alternandoBot || !agentId}
                            aria-pressed={botAtivo}
                            title={!agentId
                                ? 'Salve o agente para poder ativá-lo.'
                                : botAtivo
                                    ? 'A IA está respondendo às novas mensagens. Clique para pausar.'
                                    : 'A IA está pausada: novas mensagens não recebem resposta automática.'}
                        >
                            <span className="ax-switch-txt">
                                {alternandoBot ? 'Aguarde…' : botAtivo ? 'Respondendo' : 'Pausado'}
                            </span>
                            <span className="ax-switch-track"><i /></span>
                        </button>

                        {/* Só no celular: no computador o ensaio está sempre à vista,
                            na terceira coluna da grade. */}
                        {celular && (
                            <button type="button" className="ax-btn" onClick={() => setTestando(true)} disabled={!agentId}
                                title={agentId ? undefined : 'Salve o agente para poder testá-lo.'}>
                                <LuBot size={13} /> Testar
                            </button>
                        )}

                        <button type="button" className="ax-btn" onClick={descartar} disabled={salvando}>
                            Descartar
                        </button>
                        <button type="button" className="ax-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                }
            />

            <div className="page-body ax-fill">
                <div className={`ax ${celular && testando ? 'is-testando' : ''}`}>
                    <nav className="ax-nav" aria-label="Seções do agente">
                        <span className="ax-nav-tit">Seções</span>
                        {SECOES.map(s => {
                            const n = contagemDaSecao(s.id);
                            const bloqueada = listasBloqueadas && precisaDoAgente(s.id);
                            return (
                                <button
                                    key={s.id} type="button"
                                    className={`ax-nav-item ${secaoAtiva === s.id ? 'is-on' : ''}`}
                                    onClick={() => irPara(s.id)}
                                    disabled={bloqueada}
                                    title={bloqueada ? 'Salve o agente primeiro' : undefined}
                                >
                                    {s.icone}
                                    <span className="ax-nav-lbl">{s.label}</span>
                                    {n !== null && n > 0 && <span className="ax-nav-num">{n}</span>}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="ax-col" ref={refColuna}>
                        {carregando ? (
                            <div className="ax-carregando">
                                {Array.from({ length: 5 }).map((_, i) => <div key={i} className="ax-skeleton" />)}
                            </div>
                        ) : (
                            <>
                                {/* ─── Identidade ─────────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'identidade'}>
                                    <h2 className="ax-sec-tit"><LuUserRound size={14} /> Identidade</h2>

                                    <div className="ax-campo ax-w-nome">
                                        <label className="ax-lab" htmlFor="nomeAgente">Nome do assistente</label>
                                        <input
                                            id="nomeAgente" type="text" className="ax-inp"
                                            value={form.nomeAgente}
                                            onChange={e => setForm(p => ({ ...p, nomeAgente: e.target.value }))}
                                            placeholder="Ex.: Téo"
                                        />
                                        <span className="ax-hint">
                                            Use um nome próprio real. Apelidos, elogios ou nomes de time serão ignorados pela IA.
                                        </span>
                                    </div>

                                    <div className="ax-dupla">
                                        <div className="ax-campo ax-w-sel">
                                            <label className="ax-lab" htmlFor="tomVoz">Tom de voz</label>
                                            <Select
                                                id="tomVoz"
                                                value={String(form.tomVoz)}
                                                options={TOM_VOZ_OPTIONS.map(o => ({ value: String(o.value), label: o.label }))}
                                                onChange={v => setForm(p => ({ ...p, tomVoz: Number(v) }))}
                                            />
                                            <span className="ax-hint">Como a IA fala com o cliente.</span>
                                        </div>

                                        <div className="ax-campo ax-w-sel">
                                            <label className="ax-lab" htmlFor="objetivoPrincipal">Objetivo principal</label>
                                            <Select
                                                id="objetivoPrincipal"
                                                value={String(form.objetivoPrincipal)}
                                                options={OBJETIVO_PRINCIPAL_OPTIONS.map(o => ({ value: String(o.value), label: o.label }))}
                                                onChange={v => setForm(p => ({ ...p, objetivoPrincipal: Number(v) }))}
                                            />
                                            <span className="ax-hint">Para onde ela conduz a conversa.</span>
                                        </div>
                                    </div>
                                </section>

                                {/* ─── Abertura ───────────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'abertura'}>
                                    <h2 className="ax-sec-tit"><LuHand size={14} /> Abertura</h2>
                                    <p className="ax-sec-sub">
                                        O primeiro balão de toda conversa nova. Ligado, esse texto é enviado
                                        exatamente como está, sem passar pela IA — que assume a partir da resposta
                                        do cliente.
                                    </p>

                                    <div className="ax-campo">
                                        <label className="ax-lab" htmlFor="aberturaAtiva">Abertura fixa</label>
                                        <button
                                            type="button"
                                            id="aberturaAtiva"
                                            className={`ax-switch ${form.aberturaAtiva ? 'is-on' : ''}`}
                                            onClick={() => setForm(p => ({ ...p, aberturaAtiva: !p.aberturaAtiva }))}
                                            aria-pressed={form.aberturaAtiva}
                                        >
                                            <span className="ax-switch-txt">{form.aberturaAtiva ? 'Ligada' : 'Desligada'}</span>
                                            <span className="ax-switch-track"><i /></span>
                                        </button>
                                        <span className="ax-hint">Desligada, a IA responde desde a primeira mensagem.</span>
                                    </div>

                                    <div className="ax-campo ax-w-texto">
                                        <label className="ax-lab" htmlFor="mensagemAbertura">Mensagem de abertura</label>
                                        <span className="ax-hint ax-hint-topo">
                                            Vai numa única mensagem, com as quebras de linha que você escrever.
                                        </span>
                                        <div className="ax-ab-barra">
                                            <div ref={emojiRef} className="ax-ab-emoji">
                                                <button type="button" className="ax-chip" onClick={() => setEmojiAberto(v => !v)}
                                                    aria-expanded={emojiAberto}>
                                                    <LuSmile size={12} /> Emoji
                                                </button>
                                                {emojiAberto && (
                                                    <div className="ax-ab-pop">
                                                        <EmojiPicker
                                                            onEmojiClick={inserirEmoji}
                                                            theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
                                                            searchPlaceHolder="Buscar emoji"
                                                            width={320}
                                                            height={360}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <button type="button" className="ax-chip" onClick={() => inserirNaAbertura('\n')}>
                                                <LuPlus size={12} /> Quebra de linha
                                            </button>
                                        </div>
                                        <textarea
                                            id="mensagemAbertura" ref={refAbertura} className="ax-ta" rows={7}
                                            value={form.mensagemAbertura || ''}
                                            maxLength={MAX_ABERTURA}
                                            onChange={e => setForm(p => ({ ...p, mensagemAbertura: e.target.value }))}
                                            placeholder={ABERTURA_EXEMPLO}
                                        />
                                        <span className="ax-conta">{(form.mensagemAbertura || '').length} / {MAX_ABERTURA}</span>
                                    </div>

                                    {!!form.mensagemAbertura?.trim() && (
                                        <div className="ax-campo ax-w-texto">
                                            <span className="ax-lab">Como chega no WhatsApp</span>
                                            <div className="ax-ab-previa">
                                                <div className="ax-ab-balao">{form.mensagemAbertura}</div>
                                            </div>
                                        </div>
                                    )}
                                </section>

                                {/* ─── Conhecimento ───────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'conhecimento'}>
                                    <h2 className="ax-sec-tit"><LuBookOpen size={14} /> Conhecimento</h2>

                                    <div className="ax-campo ax-w-texto">
                                        <label className="ax-lab" htmlFor="sobreEmpresa">Sobre a empresa</label>
                                        <textarea
                                            id="sobreEmpresa" className="ax-ta" rows={6}
                                            value={form.sobreEmpresa}
                                            maxLength={MAX_TEXTO}
                                            onChange={e => setForm(p => ({ ...p, sobreEmpresa: e.target.value }))}
                                            placeholder={SOBRE_EMPRESA_EXEMPLO}
                                        />
                                        <span className="ax-conta">{form.sobreEmpresa.length} / {MAX_TEXTO}</span>
                                    </div>

                                    <div className="ax-campo ax-w-texto">
                                        <label className="ax-lab" htmlFor="instrucoesAdicionais">
                                            Instruções adicionais <span className="ax-opcional">opcional</span>
                                        </label>
                                        <span className="ax-hint ax-hint-topo">
                                            Use apenas se as outras seções não cobrirem sua necessidade.
                                        </span>
                                        <div className="ax-chips">
                                            {INSTRUCOES_SUGESTOES.map(t => (
                                                <button key={t} type="button" className="ax-chip" onClick={() => inserirInstrucao(t)}>
                                                    <LuLightbulb size={12} /> {t}
                                                </button>
                                            ))}
                                        </div>
                                        <textarea
                                            id="instrucoesAdicionais" ref={refInstrucoes} className="ax-ta" rows={4}
                                            value={form.instrucoesAdicionais || ''}
                                            maxLength={MAX_TEXTO}
                                            onChange={e => setForm(p => ({ ...p, instrucoesAdicionais: e.target.value }))}
                                            placeholder="Ex.: sempre perguntar o CEP antes de falar de frete."
                                        />
                                        <span className="ax-conta">{(form.instrucoesAdicionais || '').length} / {MAX_TEXTO}</span>
                                    </div>
                                </section>

                                {/* ─── FAQ ────────────────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'faq'}>
                                    <h2 className="ax-sec-tit">
                                        <LuMessageCircleQuestion size={14} /> Perguntas frequentes
                                        {faqs.length > 0 && <span className="ax-sec-num">{faqs.length}</span>}
                                    </h2>
                                    <p className="ax-sec-sub">Respostas prontas para o que o cliente mais pergunta.</p>

                                    {listasBloqueadas ? (
                                        <p className="ax-bloqueada"><LuInfo size={13} /> Salve o agente para cadastrar perguntas.</p>
                                    ) : (
                                        <>
                                            <div className="ax-lista">
                                                {faqsOrdenadas.map((faq, i) => faqEmEdicao === faq.id ? (
                                                    <div key={faq.id} className="ax-li is-edit">
                                                        <div className="ax-li-form">
                                                            <input
                                                                type="text" className="ax-inp" placeholder="Pergunta"
                                                                value={rascunhoFaq.pergunta}
                                                                onChange={e => setRascunhoFaq(p => ({ ...p, pergunta: e.target.value }))}
                                                            />
                                                            <textarea
                                                                className="ax-ta" rows={3} placeholder="Resposta"
                                                                value={rascunhoFaq.resposta}
                                                                onChange={e => setRascunhoFaq(p => ({ ...p, resposta: e.target.value }))}
                                                            />
                                                            <div className="ax-li-acts">
                                                                <button type="button" className="ax-btn" onClick={fecharFaq}>Cancelar</button>
                                                                <button type="button" className="ax-btn is-primary" onClick={salvarFaq} disabled={salvandoFaq}>
                                                                    {salvandoFaq ? 'Salvando…' : 'Salvar pergunta'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={faq.id} className="ax-li is-faq">
                                                        <div className="ax-li-txt">
                                                            <p className="ax-faq-p">{faq.pergunta}</p>
                                                            <p className="ax-faq-r">{faq.resposta}</p>
                                                        </div>
                                                        <div className="ax-li-icones">
                                                            <button type="button" className="ax-ic" onClick={() => moverFaq(i, 'cima')}
                                                                disabled={i === 0 || reordenando} aria-label="Subir">
                                                                <LuChevronUp size={13} />
                                                            </button>
                                                            <button type="button" className="ax-ic" onClick={() => moverFaq(i, 'baixo')}
                                                                disabled={i === faqsOrdenadas.length - 1 || reordenando} aria-label="Descer">
                                                                <LuChevronDown size={13} />
                                                            </button>
                                                            <button type="button" className="ax-ic" onClick={() => abrirFaq(faq)} aria-label="Editar">
                                                                <LuPencil size={13} />
                                                            </button>
                                                            <button type="button" className="ax-ic is-danger" onClick={() => excluirFaq(faq.id)} aria-label="Excluir">
                                                                <LuTrash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}

                                                {faqEmEdicao === 'nova' && (
                                                    <div className="ax-li is-edit">
                                                        <div className="ax-li-form">
                                                            <input
                                                                type="text" className="ax-inp" placeholder="Ex.: Qual o prazo de entrega?"
                                                                value={rascunhoFaq.pergunta} autoFocus
                                                                onChange={e => setRascunhoFaq(p => ({ ...p, pergunta: e.target.value }))}
                                                            />
                                                            <textarea
                                                                className="ax-ta" rows={3} placeholder="Ex.: Em média 5 dias úteis."
                                                                ref={refRespostaFaq}
                                                                value={rascunhoFaq.resposta}
                                                                onChange={e => setRascunhoFaq(p => ({ ...p, resposta: e.target.value }))}
                                                            />
                                                            <div className="ax-li-acts">
                                                                <button type="button" className="ax-btn" onClick={fecharFaq}>Cancelar</button>
                                                                <button type="button" className="ax-btn is-primary" onClick={salvarFaq} disabled={salvandoFaq}>
                                                                    {salvandoFaq ? 'Salvando…' : 'Salvar pergunta'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {faqs.length === 0 && faqEmEdicao !== 'nova' && (
                                                    <p className="ax-vazio">Nenhuma pergunta cadastrada ainda.</p>
                                                )}
                                            </div>

                                            {/* Perguntas que o modelo sugeriu e que ninguém respondeu
                                                ainda. Somem uma a uma conforme viram FAQ, e a seção
                                                inteira some quando acabam — sugestão que fica depois
                                                de cumprida vira ruído. */}
                                            {perguntasSugeridas.length > 0 && faqEmEdicao !== 'nova' && (
                                                <>
                                                    <p className="ax-sec-sub">
                                                        Sugestões do modelo {modeloDeOrigem?.nome}: clique para responder.
                                                    </p>
                                                    <div className="ax-chips">
                                                        {perguntasSugeridas.map(p => (
                                                            <button key={p} type="button" className="ax-chip"
                                                                onClick={() => sugerirFaq(p)}>
                                                                <LuLightbulb size={12} /> {p}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}

                                            {faqEmEdicao !== 'nova' && (
                                                <button type="button" className="ax-add" onClick={() => abrirFaq()}>
                                                    <LuPlus size={13} /> Adicionar pergunta
                                                </button>
                                            )}
                                        </>
                                    )}
                                </section>

                                {/* ─── Regras ─────────────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'regras'}>
                                    <h2 className="ax-sec-tit">
                                        <LuShieldAlert size={14} /> Regras
                                        {regras.length > 0 && <span className="ax-sec-num">{regras.length}</span>}
                                    </h2>
                                    <p className="ax-sec-sub">O que a IA nunca deve fazer — uma restrição por linha.</p>

                                    {listasBloqueadas ? (
                                        <p className="ax-bloqueada"><LuInfo size={13} /> Salve o agente para cadastrar regras.</p>
                                    ) : (
                                        <>
                                            <div className="ax-lista">
                                                {regrasOrdenadas.map((regra, i) => regraEmEdicao === regra.id ? (
                                                    <div key={regra.id} className="ax-li is-edit">
                                                        <span className="ax-ord">{i + 1}</span>
                                                        <div className="ax-li-form">
                                                            <textarea
                                                                className="ax-ta" rows={2} value={textoRegra} autoFocus
                                                                onChange={e => setTextoRegra(e.target.value)}
                                                            />
                                                            <div className="ax-li-acts">
                                                                <button type="button" className="ax-btn" onClick={fecharRegra}>Cancelar</button>
                                                                <button type="button" className="ax-btn is-primary" onClick={salvarRegra} disabled={salvandoRegra}>
                                                                    {salvandoRegra ? 'Salvando…' : 'Salvar regra'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={regra.id} className="ax-li">
                                                        <LuGripVertical size={13} className="ax-alca" aria-hidden />
                                                        <span className="ax-ord">{i + 1}</span>
                                                        <span className="ax-li-txt">{regra.descricaoRegra}</span>
                                                        <div className="ax-li-icones">
                                                            <button type="button" className="ax-ic" onClick={() => moverRegra(i, 'cima')}
                                                                disabled={i === 0 || reordenando} aria-label="Subir">
                                                                <LuChevronUp size={13} />
                                                            </button>
                                                            <button type="button" className="ax-ic" onClick={() => moverRegra(i, 'baixo')}
                                                                disabled={i === regrasOrdenadas.length - 1 || reordenando} aria-label="Descer">
                                                                <LuChevronDown size={13} />
                                                            </button>
                                                            <button type="button" className="ax-ic" onClick={() => abrirRegra(regra)} aria-label="Editar">
                                                                <LuPencil size={13} />
                                                            </button>
                                                            <button type="button" className="ax-ic is-danger" onClick={() => excluirRegra(regra.id)} aria-label="Excluir">
                                                                <LuTrash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}

                                                {regraEmEdicao === 'nova' && (
                                                    <div className="ax-li is-edit">
                                                        <span className="ax-ord">{regras.length + 1}</span>
                                                        <div className="ax-li-form">
                                                            <div className="ax-chips">
                                                                {REGRA_SUGESTOES.map(t => (
                                                                    <button key={t} type="button" className="ax-chip" onClick={() => setTextoRegra(t)}>
                                                                        <LuLightbulb size={12} /> {t}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <textarea
                                                                className="ax-ta" rows={2} value={textoRegra} autoFocus
                                                                placeholder="Ex.: Nunca prometa prazos de entrega."
                                                                onChange={e => setTextoRegra(e.target.value)}
                                                            />
                                                            <div className="ax-li-acts">
                                                                <button type="button" className="ax-btn" onClick={fecharRegra}>Cancelar</button>
                                                                <button type="button" className="ax-btn is-primary" onClick={salvarRegra} disabled={salvandoRegra}>
                                                                    {salvandoRegra ? 'Salvando…' : 'Salvar regra'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {regras.length === 0 && regraEmEdicao !== 'nova' && (
                                                    <p className="ax-vazio">Nenhuma regra cadastrada ainda.</p>
                                                )}
                                            </div>

                                            {regraEmEdicao !== 'nova' && (
                                                <button type="button" className="ax-add" onClick={() => abrirRegra()}>
                                                    <LuPlus size={13} /> Adicionar regra
                                                </button>
                                            )}
                                        </>
                                    )}
                                </section>

                                {/* ─── Transbordo ─────────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'transbordo'}>
                                    <h2 className="ax-sec-tit"><LuArrowRightLeft size={14} /> Transbordo</h2>
                                    <p className="ax-sec-sub">Quando a IA deve parar e passar a conversa para uma pessoa.</p>

                                    <div className="ax-campo ax-w-texto">
                                        <div className="ax-chips">
                                            {TRANSBORDO_SUGESTOES.map(t => (
                                                <button key={t} type="button" className="ax-chip" onClick={() => somarTransbordo(t)}>
                                                    <LuLightbulb size={12} /> {t}
                                                </button>
                                            ))}
                                        </div>
                                        <textarea
                                            id="condicoesTransbordo" className="ax-ta" rows={4}
                                            value={form.condicoesTransbordo}
                                            maxLength={MAX_TEXTO}
                                            onChange={e => setForm(p => ({ ...p, condicoesTransbordo: e.target.value }))}
                                            placeholder="Ex.: quando o cliente pedir para falar com um humano."
                                        />
                                        <span className="ax-conta">{form.condicoesTransbordo.length} / {MAX_TEXTO}</span>
                                    </div>
                                </section>

                                {/* ─── Roteamento ─────────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'roteamento'}>
                                    <h2 className="ax-sec-tit">
                                        <LuRoute size={14} /> Roteamento
                                        {roteamentos.length > 0 && <span className="ax-sec-num">{roteamentos.length}</span>}
                                    </h2>
                                    <p className="ax-sec-sub">
                                        Para quem a conversa vai quando a IA transferir, conforme o motivo do contato.
                                    </p>

                                    {listasBloqueadas ? (
                                        <p className="ax-bloqueada"><LuInfo size={13} /> Salve o agente para configurar o roteamento.</p>
                                    ) : (
                                        <>
                                            {/* Sem este aviso a pessoa cadastra "defeito → Carlos"
                                                achando que a IA passa na hora, e ela pode tentar
                                                resolver sozinha — o Carlos nunca recebe. */}
                                            <div className="ax-aviso">
                                                <LuInfo size={13} />
                                                <span>
                                                    A regra decide <strong>para quem</strong> a conversa vai. <strong>Quando</strong> a
                                                    IA transfere continua nas condições de{' '}
                                                    <button type="button" className="ax-link" onClick={() => irPara('transbordo')}>Transbordo</button>.
                                                    {' '}Conexões com dono exclusivo continuam indo para o dono.
                                                </span>
                                            </div>

                                            <div className="ax-lista">
                                                {roteamentos.map(r => roteamentoEmEdicao === r.id ? (
                                                    <div key={r.id} className="ax-li is-edit">
                                                        {formularioRoteamento(false)}
                                                    </div>
                                                ) : (
                                                    <div key={r.id} className={`ax-li ax-rt ${r.ativa ? '' : 'is-off'}`}>
                                                        <div className="ax-li-txt">
                                                            <p className="ax-rt-int">{r.intencao}</p>
                                                            <p className="ax-rt-dest">
                                                                <LuArrowRight size={12} className="ax-rt-seta" aria-hidden />
                                                                <span className={`ax-rt-alvo ${r.destinoValido ? '' : 'is-bad'}`}>
                                                                    {r.tipoDestino === TIPO_DESTINO.Equipe
                                                                        ? <LuUsers size={12} />
                                                                        : <LuUserRound size={12} />}
                                                                    {r.destinoNome
                                                                        || (r.tipoDestino === TIPO_DESTINO.Equipe ? 'Equipe removida' : 'Pessoa removida')}
                                                                </span>
                                                                {!r.destinoValido && (
                                                                    <span className="ax-badge is-warn"
                                                                        title="A pessoa foi excluída ou a equipe ficou sem membros. Enquanto isso, as conversas deste assunto vão para o rodízio geral.">
                                                                        <LuTriangleAlert size={11} /> Destino inválido
                                                                    </span>
                                                                )}
                                                                {!r.ativa && <span className="ax-badge is-off">Inativa</span>}
                                                            </p>
                                                        </div>
                                                        <div className="ax-li-icones">
                                                            {r.destinoValido && (
                                                                <button type="button" className="ax-ic"
                                                                    onClick={() => alternarRoteamento(r)}
                                                                    disabled={alternandoRoteamento === r.id}
                                                                    aria-label={r.ativa ? 'Desativar' : 'Ativar'}
                                                                    title={r.ativa ? 'Desativar: as conversas deste assunto voltam ao rodízio.' : 'Ativar'}>
                                                                    {r.ativa ? <LuPowerOff size={13} /> : <LuPower size={13} />}
                                                                </button>
                                                            )}
                                                            <button type="button" className="ax-ic" onClick={() => abrirRoteamento(r)}
                                                                aria-label={r.destinoValido ? 'Editar' : 'Trocar destino'}
                                                                title={r.destinoValido ? undefined : 'Trocar destino'}>
                                                                <LuPencil size={13} />
                                                            </button>
                                                            <button type="button" className="ax-ic is-danger" onClick={() => excluirRoteamento(r.id)} aria-label="Excluir">
                                                                <LuTrash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}

                                                {roteamentoEmEdicao === 'nova' && (
                                                    <div className="ax-li is-edit">
                                                        {formularioRoteamento(true)}
                                                    </div>
                                                )}

                                                {roteamentos.length === 0 && roteamentoEmEdicao !== 'nova' && (
                                                    <p className="ax-vazio">
                                                        Nenhum roteamento. As transferências seguem o rodízio normal da empresa.
                                                    </p>
                                                )}
                                            </div>

                                            {roteamentoEmEdicao !== 'nova' && (
                                                <button type="button" className="ax-add" onClick={() => abrirRoteamento()}>
                                                    <LuPlus size={13} /> Adicionar roteamento
                                                </button>
                                            )}
                                        </>
                                    )}
                                </section>

                                {/* ─── Histórico ──────────────────────────── */}
                                <section className="ax-sec" hidden={secaoAtiva !== 'historico'}>
                                    <h2 className="ax-sec-tit"><LuHistory size={14} /> Histórico</h2>
                                    {agentId && secaoAtiva === 'historico' && (
                                        <SecaoHistorico entidade="agente" id={agentId} variante="pagina" />
                                    )}
                                </section>
                            </>
                        )}
                    </div>

                    <TestAgentChat agenteId={agentId} agenteNome={form.nomeAgente} />

                    {celular && testando && (
                        <button type="button" className="ax-fecha-teste" onClick={() => setTestando(false)}>
                            <LuX size={15} /> Fechar o ensaio
                        </button>
                    )}
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmacao.aberta}
                onClose={() => setConfirmacao(p => ({ ...p, aberta: false }))}
                onConfirm={confirmacao.aoConfirmar}
                title={confirmacao.titulo}
                message={confirmacao.mensagem}
            />
        </div>
    );
};

export default AgentePage;
